/**
 * ChangePassword — self-service password change for the signed-in user.
 * Route: /account/password (parent wires the route)
 * API: POST /auth/change-password { current_password, new_password } — the
 * auth-service field is `current_password`, not `old_password`.
 * States: idle / saving / success / error.
 */
import { useState } from "react";
import { AppButton, AppCard, AppField, AppInput } from "../../../shared/components/ui";
import { PageHeader } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api, ApiError } from "../../../shared/api/client";

export function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!currentPassword || !newPassword) {
      setState("error");
      setError("Enter your current password and a new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setState("error");
      setError("New password and confirmation do not match");
      return;
    }
    setState("saving");
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setState("success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (ex: unknown) {
      setState("error");
      setError(
        ex instanceof ApiError ? ex.message
          : ex instanceof Error ? ex.message
            : "Failed to change password",
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Change Password"
        subtitle="Update your sign-in password. Your current session stays active."
      />

      <AppCard style={{ maxWidth: 420 }}>
        <form onSubmit={submit}>
          <AppField label="Current password">
            <AppInput
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              disabled={state === "saving"}
              autoFocus
            />
          </AppField>
          <AppField label="New password">
            <AppInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Min 8 characters"
              disabled={state === "saving"}
            />
          </AppField>
          <AppField label="Confirm new password" error={state === "error" ? error : undefined}>
            <AppInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={state === "saving"}
            />
          </AppField>

          {state === "success" && (
            <p className="pg-text-muted"><IconCheck size={14} /> Password changed successfully.</p>
          )}

          <div className="pg-modal-actions">
            <AppButton type="submit" disabled={state === "saving"}>
              {state === "saving" ? <><IconSpinner size={14} /> Saving…</> : "Change Password"}
            </AppButton>
          </div>
        </form>
      </AppCard>
    </div>
  );
}
